import { useContext, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
  Typography,
} from "antd";
import { AuthContext } from "../../useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faSquare } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import "./LoginForm.css";

export const LoginState = {
  login: "login",
  signUp: "sign_up",
  confirm: "confirm",
  forgotPassword: "forgot_password",
  resetPassword: "reset_password",
  complete: "complete",
};

const devMode = false;

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const LoginForm = ({ redirect }) => {
  const search = useQuery();
  let initialStep = search.get("step");
  const [state, setState] = useState(initialStep || LoginState.login);
  const [email, setEmail] = useState();

  const stepTitles = {
    login: "Log In",
    sign_up: "Create an Account",
    confirm: "Confirm Your Account",
    forgot_password: "Forgot Password",
    reset_password: "Reset your password",
    complete: "",
  };

  const LoginTitle = () => {
    return (
      <Typography.Title level={4} type="secondary" style={{ margin: 0 }}>
        {stepTitles[state]}
      </Typography.Title>
    );
  };

  const LoginStep = () => {
    switch (state) {
      case LoginState.forgotPassword:
        return <ForgotPassword setState={setState} setEmail={setEmail} />;
      case LoginState.confirm:
        return <ConfirmAccount setState={setState} email={email} />;
      case LoginState.signUp:
        return <SignUp setState={setState} setEmail={setEmail} />;
      case LoginState.resetPassword:
        return <RestPasswordForm setState={setState} email={email} />;
      case LoginState.complete:
        return null;
      default:
        return (
          <Login
            redirect={redirect}
            setState={(value) => setState(value)}
            email={email}
            setEmail={setEmail}
          />
        );
    }
  };

  return (
    <>
      <Card
        style={{ maxWidth: "400px", margin: "0 auto" }}
        title={<LoginTitle />}
      >
        <LoginStep />
      </Card>
    </>
  );
};

const SignUp = ({ onSuccess, setState, setEmail }) => {
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState();
  const [validForm, setValidForm] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const { signUp } = useContext(AuthContext);

  const submit = async (values) => {
    const { email, password, firstName, lastName, organizationId } = values;

    setEmail(email);
    if (devMode) {
      setState(LoginState.confirm);
      return;
    }

    setSubmitting(true);
    await signUp(email, password, firstName, lastName, organizationId)
      .then(() => {
        setState(LoginState.confirm);
      })
      .catch((err) => {
        notification.error({ message: "Error", description: err.message });
      })
      .finally(() => setSubmitting(false));
  };

  const [form] = Form.useForm();

  const onFieldsChange = () => {
    const { email, password, confirmPassword } = form.getFieldsValue();
    if (
      !(
        email &&
        password &&
        confirmPassword &&
        password === confirmPassword &&
        validPassword
      )
    )
      setValidForm(false);
    else setValidForm(true);
  };

  return (
    <>
      <Form
        onFinish={submit}
        layout="vertical"
        onValuesChange={onFieldsChange}
        form={form}
      >
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                {
                  required: true,
                  message: "Please provide your first name",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: "Please provide your last name",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            {
              required: true,
              message: "Please provide your email",
              type: "email",
            },
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item
          name="password"
          label="Password"
          rules={[
            {
              required: true,
              message: "Please a password",
            },
          ]}
        >
          <Input
            placeholder="Password"
            type="password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>
        {password && !validPassword && (
          <Form.Item>
            <PasswordStrength
              password={password}
              setValidPassword={setValidPassword}
            />
          </Form.Item>
        )}
        <Form.Item name="confirmPassword" label="Confirm Password">
          <Input placeholder="Confirm Password" type="password" />
        </Form.Item>
        <Form.Item>
          <Button
            htmlType="submit"
            block
            type="primary"
            disabled={!validForm || submitting}
          >
            Sign Up
          </Button>
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" style={{ textAlign: "center" }}>
        Already have an account?{" "}
        <Typography.Link onClick={() => setState(LoginState.login)}>
          Sign in
        </Typography.Link>
      </Typography.Paragraph>
    </>
  );
};

const Login = ({ redirect, setState }) => {
  const { login } = useContext(AuthContext);
  const submit = async (values) => {
    const { email, password } = values;
    await login({ email: email, password: password, redirect: redirect }).catch(
      (err) => {
        notification.error({ message: "Error", description: err.message });
      }
    );
  };

  return (
    <>
      <Form onFinish={(values) => submit(values)} layout="vertical">
        <Form.Item name="email" label="Email">
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item name="password" label="Password">
          <Input type="password" placeholder="Password" />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" block type="primary">
            Sign In
          </Button>
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" style={{ textAlign: "center" }}>
        <Typography.Link onClick={() => setState(LoginState.forgotPassword)}>
          Forgot password
        </Typography.Link>
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ textAlign: "center" }}>
        Don't have an account?{" "}
        <Typography.Link onClick={() => setState(LoginState.signUp)}>
          Sign up
        </Typography.Link>
      </Typography.Paragraph>
    </>
  );
};

const ForgotPassword = ({ setState, setEmail }) => {
  const { forgotPassword } = useContext(AuthContext);
  const submit = async (values) => {
    const { email } = values;
    setEmail(email);
    if (devMode) {
      setState(LoginState.resetPassword);
      return;
    }
    await forgotPassword(values.email)
      .then((r) => setState(LoginState.resetPassword))
      .catch((err) => {
        notification.error({ message: "Error", description: err.message });
      });
  };

  return (
    <>
      <Form onFinish={(values) => submit(values)} layout="vertical">
        <Form.Item name="email" label="Email">
          <Input type="email" placeholder="Email" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" block htmlType="submit">
            Request password reset
          </Button>
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" style={{ textAlign: "center" }}>
        <Typography.Link onClick={() => setState(LoginState.login)}>
          Return to sign in
        </Typography.Link>
      </Typography.Paragraph>
    </>
  );
};

const RestPasswordForm = ({ setState, email }) => {
  const { resetPassword } = useContext(AuthContext);
  const submit = async (values) => {
    if (devMode) {
      setState(LoginState.login);
      return;
    }
    const { email, code, password } = values;
    await resetPassword(email, password, code)
      .catch((err) => {
        notification.error({ message: "Error", description: err.message });
      })
      .then(async (r) => setState(LoginState.login));
  };

  return (
    <Form
      onFinish={(values) => submit(values)}
      layout="vertical"
      initialValues={{ email: email }}
    >
      <Form.Item name="email" label="Email">
        <Input type="email" placeholder="Email" />
      </Form.Item>
      <Form.Item name="code" label="Verification Code">
        <Input placeholder="******" />
      </Form.Item>
      <Form.Item name="password" label="New Password">
        <Input type="password" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" block htmlType="submit">
          Reset Password
        </Button>
      </Form.Item>
    </Form>
  );
};

const ConfirmAccount = ({ setState, email }) => {
  const [submitting, setSubmitting] = useState(false);
  const { confirmSignup, login } = useContext(AuthContext);
  const navigate = useNavigate();

  const submit = async (values) => {
    if (devMode) setState(LoginState.login);
    const { email, code, password } = values;
    setSubmitting(true);
    await confirmSignup(email, code)
      .then(async () => {
        await login({
          email: email,
          password: password,
          redirect: "/dashboard",
        });
      })
      .catch((err) => {
        notification.error({ message: "Error", description: err.message });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Typography.Paragraph>
        A verification code was sent to the email you provided
      </Typography.Paragraph>
      <Form
        onFinish={submit}
        initialValues={{ email: email }}
        layout="vertical"
      >
        <Form.Item name="email" label="Email">
          <Input placeholder="Email" />
        </Form.Item>
        <Form.Item name="code" label="Verification Code">
          <Input placeholder="Verification Code" />
        </Form.Item>
        <Form.Item name="password" label="Password">
          <Input placeholder="Password" type="password" />
        </Form.Item>

        <Form.Item>
          <Button htmlType="submit" block type="primary" disabled={submitting}>
            Verify
          </Button>
        </Form.Item>
      </Form>
      <Typography.Paragraph type="secondary" style={{ textAlign: "center" }}>
        <Typography.Link onClick={() => setState(LoginState.signUp)}>
          Return to login
        </Typography.Link>
      </Typography.Paragraph>
    </>
  );
};

const PasswordStrength = ({ password, setValidPassword }) => {
  const [hasLength, setHasLength] = useState();
  const [hasUpper, setHasUpper] = useState();
  const [hasLower, setHasLower] = useState();
  const [hasSpecial, setHasSpecial] = useState();
  const [hasNumber, setHasNumber] = useState();

  useEffect(() => {
    if (password) {
      const validLength = password.length >= 8 ? true : false,
        validUpper = password.toLowerCase() !== password,
        validLower = password.toUpperCase() !== password,
        validNumber = /\d/.test(password),
        validSpecial = /[ `!@#$%^&*()_+\-=\]{};':"\\|,.<>?~]/.test(password);

      setHasLength(validLength);
      setHasUpper(validUpper);
      setHasLower(validLower);
      setHasNumber(validNumber);
      setHasSpecial(validSpecial);

      setValidPassword(
        validLength && validUpper && validLower && validNumber && validSpecial
      );
    }
  }, [password]);

  return (
    <>
      {password && (
        <>
          <RequirementDisplay valid={hasLength}>
            8 characters
          </RequirementDisplay>
          <RequirementDisplay valid={hasUpper}>
            Uppercase letter
          </RequirementDisplay>
          <RequirementDisplay valid={hasLower}>
            Lowercase letter
          </RequirementDisplay>
          <RequirementDisplay valid={hasNumber}>Number</RequirementDisplay>
          <RequirementDisplay valid={hasSpecial}>
            Special character
          </RequirementDisplay>
        </>
      )}
    </>
  );
};

const RequirementDisplay = ({ valid, children }) => {
  return (
    <div className={`requirement-display ${valid && "valid"}`}>
      <FontAwesomeIcon icon={valid ? faCheckSquare : faSquare} /> {children}
    </div>
  );
};

export default LoginForm;
